import * as React from "react";

import { applyAppBrandOverlay } from "../customer-branding/apply/overlay";

import ArmadaFavicon from "./assets/armada/armada-favicon.ico";
import ArmadaLogo from "./assets/armada/ArmadaLogo.svg?react";
import ICRAFavicon from "./assets/icra/icra-favicon.ico";
import ICRALogo from "./assets/icra/ICRALogo.svg?react";
import ResearchAssistFavicon from "./assets/icra/researchAssist-favicon.svg";
import ResearchAssistFaviconIcon from "./assets/icra/researchAssist-favicon.svg?react";
import ResearchAssistLogo from "./assets/icra/researchAssist-logo.png";
import MSquaredBotAvatar from "./assets/msquared/msquared-bot-avatar.svg?react";
import MSquaredFavicon from "./assets/msquared/msquared-favicon.png";
import MSquaredFaviconWhite from "./assets/msquared/msquared-favicon-white.svg";
import MSquaredLoaderSvg from "./assets/msquared/msquared-loader.svg";
import MSquaredLogo from "./assets/msquared/msquared-logo.png";
import MSquaredLogoWhite from "./assets/msquared/msquared-logo-white.svg";
import NeedlFavicon from "./assets/needl/needl-favicon.png";
import NeedlLoadingSvg from "./assets/needl/needl-loading.svg";
import NeedlLogo from "./assets/needl/needl-logo.svg?react";
import NeedlLogoFull from "./assets/needl/needl-logo-full.svg";
import NeedlLogoWhite from "./assets/needl/NeedlLogoWhite.svg";
import PwcFavicon from "./assets/pwc/pwc-favicon.ico";
import PwcLoadingSvg from "./assets/pwc/pwc-loading.svg";
import PwcLogo from "./assets/pwc/pwc-logo.svg?react";
import PwcLogoWhite from "./assets/pwc/pwc-logo-white.svg";
import SoftwareOneFavicon from "./assets/softwareone/softwareone-favicon.svg";
import SoftwareOneLogo from "./assets/softwareone/softwareone-logo.svg?react";
import SoftwareOneLogoFull from "./assets/softwareone/softwareone-logo-full.svg?react";
import { useBranding } from "./BrandingContext";
import { BRANDING, type BrandingConfig, type BrandingType } from "./types";
import { getEnvBranding } from "./utils";

export const getBrandingConfig = (
  brandingOverride?: BrandingType,
): BrandingConfig => {
  const branding = brandingOverride || getEnvBranding();
  const envTitle =
    branding === getEnvBranding() ? import.meta.env.VITE_TITLE : undefined;

  if (branding === BRANDING.PWC) {
    return {
      logo: PwcLogo,
      logoOnDark: PwcLogoWhite,
      logoFull: PwcLogo,
      logoFullOnDark: PwcLogoWhite,
      favicon: PwcFavicon,
      name: envTitle || "PwC",
      scAppName: "Client Intelligence and Insights Hub",
      disclaimerAppName: "Client Intelligence and Insights Hub",
      showName: false,
      chatLoadingLogo: PwcLoadingSvg,
      scLandingHeadlines: [
        "How can I help today?",
        "Search across engagement letters",
        "Check the scope of an engagement",
        "What fees and terms were agreed?",
        "Let's prepare a client brief",
      ],
    };
  }

  if (branding === BRANDING.SOFTWARE_ONE) {
    return {
      logo: SoftwareOneLogo,
      logoFull: SoftwareOneLogoFull,
      logoFullClassName: "h-9 w-[176px]",
      favicon: SoftwareOneFavicon,
      name: "SoftwareOne",
      scAppName: "Credit Analysis",
      disclaimerAppName: "Credit Analysis",
      showName: false,
      loginWelcomeText: <>Welcome to Credit Analysis</>,
      loginSubHeadingText: "Powered by SoftwareOne",
      scLandingHeadlines: [
        "How can I help today?",
        "Let's review a credit proposal",
        "Check the borrower's financials",
        "What are the key credit risks?",
      ],
    };
  }

  if (branding === BRANDING.MSQUARED) {
    return {
      logo: MSquaredLogo,
      logoOnDark: MSquaredFaviconWhite,
      logoFull: MSquaredLogo,
      logoFullOnDark: MSquaredLogoWhite,
      logoFullClassName: "h-6",
      favicon: MSquaredFavicon,
      name: envTitle || "M-Squared",
      scAppName: "M-Squared",
      disclaimerAppName: "M-Squared AI",
      showName: false,
      botAvatar: MSquaredBotAvatar,
      chatLoadingLogo: MSquaredLoaderSvg,
      scLandingPlaceholders: [
        "Ask about your channel performance...",
        "Ask about your ROAS trends...",
        "Ask about your masterclass content...",
        "Ask about your campaign ROI...",
      ],
      scLandingHeadlines: [
        "How can I help today?",
        "Let's review channel performance",
        "Analyze ROAS trends",
        "What is driving revenue?",
        "Summarize the latest MMM study",
      ],
    };
  }

  if (branding === BRANDING.ICRA) {
    return {
      logo: ICRAFavicon,
      logoFull: ICRALogo,
      favicon: ICRAFavicon,
      name:
        envTitle ||
        "Credit-IQ | ICRA’s Structured Intelligence for Credit Analysis",
      scAppName: "Research Assist",
      disclaimerAppName: "Research Assist",
      showName: false,
      loginWelcomeText: <>Welcome to Credit-IQ</>,
      loginSubHeadingText: "ICRA’s Structured Intelligence for Credit Analysis",
      scLandingHeadlines: [
        "How can I help today?",
        "Look up a company's credit profile",
        "Let's compare issuers in a sector",
        "Explain a rating rationale",
      ],
      loginFooterContent: (
        <p className="text-center text-sm text-muted-foreground">
          Copyright © 2026 ICRA Limited. All Rights Reserved
        </p>
      ),
    };
  }

  if (branding === BRANDING.ICRA_RESEARCH_ASSIST) {
    return {
      logo: ResearchAssistLogo,
      logoFull: ResearchAssistLogo,
      logoFullClassName: "h-6",
      favicon: ResearchAssistFavicon,
      name: envTitle || "Research Assist | ICRA An Affiliate of Moody's",
      scAppName: "Research Assist",
      disclaimerAppName: "Research Assist",
      showName: false,
      botAvatar: ResearchAssistFaviconIcon,
      loginWelcomeText: "Welcome to Research Assist",
      loginFooterContent: (
        <p className="text-center text-sm text-muted-foreground">
          Copyright © 2026 ICRA Limited. All Rights Reserved
        </p>
      ),
    };
  }

  if (branding === BRANDING.ARMADA) {
    return {
      logo: ArmadaFavicon,
      logoFull: ArmadaLogo,
      favicon: ArmadaFavicon,
      name: envTitle || "Armada",
      scAppName: "AskArmada",
      disclaimerAppName: "AskArmada",
      showName: false,
    };
  }

  return {
    logo: NeedlLogo,
    logoFull: NeedlLogoFull,
    logoFullOnDark: NeedlLogoWhite,
    logoFullClassName: "h-6",
    favicon: NeedlFavicon,
    name: envTitle || "needl.ai",
    scAppName: "AskNeedl",
    disclaimerAppName: "AskNeedl",
    showName: false,
    chatLoadingLogo: NeedlLoadingSvg,
    scLandingHeadlines: [
      "How can I help today?",
      "Search your documents",
      "What changed in the latest filings?",
      "Let's compare companies side by side",
      "Ask a question about your data",
    ],
    autoDdPitch: {
      product: {
        name: "AI Due Diligence",
        tagline: "Evidence-first Analysis",
      },
      headline: "Due diligence in hours, not weeks.",
      body: "AI Due Diligence reads every document in the data room, models, contracts, and filings, and turns them into decision-ready analysis. Deals move faster, and junior analysts deliver senior-grade work.",
      points: [
        "Hundreds of analyst hours saved on every deal",
        "Red flags, commercials, legal and covenants in one dashboard",
        "Every figure traces to the exact source page that supports it",
      ],
      trustPoints: [
        "Your data stays confidential",
        "Enterprise-grade security",
        "Source-linked answers",
      ],
    },
  };
};

export const useBrandingConfig = (): BrandingConfig => {
  const { activeBranding, appBrand } = useBranding();
  return React.useMemo(() => {
    const base = getBrandingConfig(activeBranding);
    return appBrand ? applyAppBrandOverlay(base, appBrand) : base;
  }, [activeBranding, appBrand]);
};
